// resources/js/Components/ZoneCard.jsx
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {
    Autocomplete,
    Badge,
    Box,
    Button,
    Card,
    CardContent,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    Grid,
    MenuItem,
    Switch,
    TextField,
    Tooltip,
    IconButton,
    Typography
} from '@mui/material';
import {
    AccessTime,
    Check,
    ContentCopy,
    Delete,
    Insights as InsightsIcon,
    Merge as MergeIcon,
    RadioButtonChecked,
    RadioButtonUnchecked,
    Sensors as SensorsIcon,
    Settings,
    TrendingFlat,
    VerticalAlignTop
} from '@mui/icons-material';
import ScheduleSummary from './ScheduleSummary';
import ZoneSummaryAdvanced from "./ZoneSummaryAdvanced";
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from '@mui/icons-material/Edit'; // replaces PenToolIcon
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PeopleIcon from '@mui/icons-material/People';
import NotificationsIcon from '@mui/icons-material/Notifications';
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

/**
 * Normalizes data coming from Blade data attributes or legacy events so filters always receive arrays.
 */
const ensureArray = (value) => {
    if (Array.isArray(value)) {
        return value;
    }

    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            return [];
        }
    }

    if (value && typeof value === 'object') {
        return Object.values(value);
    }

    return [];
};

/**
 * Converts any iterable/tag collection into a flat list of string ids for consistent comparisons.
 */
const ensureIdList = (value) => ensureArray(value)
    .map((entry) => {
        if (entry == null) {
            return null;
        }

        if (typeof entry === 'object' && Object.prototype.hasOwnProperty.call(entry, 'id')) {
            return entry.id != null ? entry.id.toString() : null;
        }

        return entry?.toString?.() ?? null;
    })
    .filter(Boolean);

/**
 * Default values used when promoting simple alarms into managed rules.
 */
const defaultPromotionConfig = {
    min_duration: 1,
    alarm_frequency: '',
};

const normalizeZoneDirection = (value) => {
    if (!value || typeof value !== 'object') {
        return {};
    }

    const start = Array.isArray(value?.start) ? value.start : [];
    const stop = Array.isArray(value?.stop) ? value.stop : [];
    const startX = Number(start[0] ?? start?.x);
    const startY = Number(start[1] ?? start?.y);
    const stopX = Number(stop[0] ?? stop?.x);
    const stopY = Number(stop[1] ?? stop?.y);

    if (!Number.isFinite(startX) || !Number.isFinite(startY) || !Number.isFinite(stopX) || !Number.isFinite(stopY)) {
        return {};
    }

    const countsDirSource = value?.counts_dir && typeof value.counts_dir === 'object' ? value.counts_dir : {};
    const countsDir = Object.entries(countsDirSource).reduce((carry, [className, mode]) => {
        if (typeof className !== 'string' || className.trim() === '') {
            return carry;
        }

        if (!['forward', 'backward', 'both'].includes(mode)) {
            return carry;
        }

        carry[className] = mode;
        return carry;
    }, {});

    return {
        start: [Number.parseFloat(Math.min(Math.max(startX, 0), 1).toFixed(6)), Number.parseFloat(Math.min(Math.max(startY, 0), 1).toFixed(6))],
        stop: [Number.parseFloat(Math.min(Math.max(stopX, 0), 1).toFixed(6)), Number.parseFloat(Math.min(Math.max(stopY, 0), 1).toFixed(6))],
        counts_dir: countsDir,
    };
};

const normalizeZoneGpio = (value) => {
    if (!value || typeof value !== 'object') {
        return {};
    }

    const id = Number(value?.id);
    const name = typeof value?.name === 'string' ? value.name.trim() : '';
    const delayValue = Number(value?.delay ?? 0);
    const classes = Array.isArray(value?.classes)
        ? value.classes.map((entry) => (typeof entry === 'string' ? entry.trim() : '')).filter(Boolean)
        : [];
    const freeClasses = Array.isArray(value?.free_classes)
        ? value.free_classes.map((entry) => (typeof entry === 'string' ? entry.trim() : '')).filter(Boolean)
        : [];

    if (!Number.isFinite(id) || id <= 0 || !name) {
        return {};
    }

    return {
        id,
        name,
        delay: Number.isFinite(delayValue) && delayValue >= 0 ? Number(delayValue.toFixed(2)) : 0,
        classes: Array.from(new Set(classes)),
        free_classes: Array.from(new Set(freeClasses)),
    };
};

const normalizeZoneAudioMessage = (value) => {
    const normalized = typeof value === 'string' ? value : '';
    return normalized.length > 2000 ? normalized.slice(0, 2000) : normalized;
};

const hasZoneDirection = (value) => {
    const direction = normalizeZoneDirection(value);
    return Array.isArray(direction.start) && direction.start.length === 2 && Array.isArray(direction.stop) && direction.stop.length === 2;
};

const directionModeLabel = (value) => {
    if (value === 'forward') {
        return 'Forward';
    }
    if (value === 'backward') {
        return 'Backward';
    }
    if (value === 'both') {
        return 'Both';
    }
    return 'Disabled';
};

const ZoneCard = ({
                      index,
                      zone,
                      trackedTags,
                      selectedTags,
                      fixedTags,
                      selectedModel,
                      allTags,
                      gpioGates = [],
                      trafficLights = [],
                      markerSets = [],
                      disabled,
                      expanded,
                      onUpdate,
                      onDelete,
                      onClone,
                      onSchedule,
                      onSettings,
                      onPromoteSimpleAlarms,
                      hidePromotionAction = false,
                      isFocused = false,
                      focusEventId = 0,
                      onExpandStateChange,
                      isReperSelected = false,
                      onToggleReperZone,
                      isDirectionEnabled = false,
                      onToggleDirectionZone,
                      onEditDirectionZone,
                      selectedHornId = -1,
                      audioMessageEnabled = false,
                  }) => {
    const inputIndex = index ;
    const domIndex = zone?.originalIndex ?? zone?.original_index ?? index;
    const resolvedZoneIndex = useMemo(() => {
        const numericIndex = Number(domIndex);
        if (Number.isInteger(numericIndex)) {
            return numericIndex;
        }
        return Number.isInteger(index) ? index : 0;
    }, [domIndex, index]);
    const normalizedAllTags = useMemo(() => ensureArray(allTags).filter(Boolean), [allTags]);
    const trackedTagIds = useMemo(() => ensureIdList(trackedTags), [trackedTags]);
    const selectedTagIds = useMemo(() => ensureIdList(selectedTags), [selectedTags]);
    const fixedTagIds = useMemo(() => ensureIdList(fixedTags), [fixedTags]);
    const normalizedAdvancedSettings = useMemo(() => {
        if (!zone?.advanced_settings) {
            return {};
        }

        if (typeof zone.advanced_settings === 'string') {
            try {
                return JSON.parse(zone.advanced_settings) || {};
            } catch (error) {
                console.warn('Failed to parse advanced_settings JSON for zone', zone?.id, error);
                return {};
            }
        }

        if (typeof zone.advanced_settings === 'object') {
            return zone.advanced_settings;
        }

        return {};
    }, [zone.advanced_settings, zone?.id]);
    const [advanced, setAdvanced] = useState(!!zone.advanced);
    const [drawZone, setDrawZone] = useState(!!(zone?.draw_zone));
    const [showCountTags, setShowCountTags] = useState([]);
    const [showAlarmTags, setShowAlarmTags] = useState([]);
    const [expandedSection, setExpandedSection] = useState(!!expanded);
    const [promotionDialogOpen, setPromotionDialogOpen] = useState(false);
    const [promotionConfig, setPromotionConfig] = useState(defaultPromotionConfig);
    const currentDirectionConfig = useMemo(() => {
        const directDirection = normalizeZoneDirection(zone?.direction);
        if (hasZoneDirection(directDirection)) {
            return directDirection;
        }

        const meta = zone?.meta && typeof zone.meta === 'object' ? zone.meta : {};
        return normalizeZoneDirection(meta?.direction);
    }, [zone]);
    const currentGpioConfig = useMemo(() => {
        const directGpio = normalizeZoneGpio(zone?.gpio);
        if (directGpio.id) {
            return directGpio;
        }

        const meta = zone?.meta && typeof zone.meta === 'object' ? zone.meta : {};
        return normalizeZoneGpio(meta?.gpio);
    }, [zone]);
    const currentAudioMessage = useMemo(() => {
        const meta = zone?.meta && typeof zone.meta === 'object' ? zone.meta : {};
        return normalizeZoneAudioMessage(zone?.audio_message ?? meta?.audio_message ?? '');
    }, [zone]);
    const zoneMetaValue = useMemo(() => {
        const meta = zone?.meta && typeof zone.meta === 'object' ? zone.meta : {};
        const directReper = zone?.reper && typeof zone.reper === 'object' ? zone.reper : {};
        const directPoint = Array.isArray(directReper?.point) ? directReper.point : [];
        const hasDirectReper = directPoint.length >= 2 && Number.isFinite(Number(directReper?.range ?? directReper?.reper_range ?? directReper?.radius_scaled));
        const reper = hasDirectReper
            ? directReper
            : (meta?.reper && typeof meta.reper === 'object' ? meta.reper : {});

        return JSON.stringify({
            ...meta,
            reper,
            direction: currentDirectionConfig,
            gpio: currentGpioConfig,
            audio_message: currentAudioMessage,
        });
    }, [currentAudioMessage, currentDirectionConfig, currentGpioConfig, zone]);

    // add a use effect for advanced if the toggle switch is activated make sure default structure is pressent to zone.advanced_settings
    useEffect(() => {
        if (!advanced) {
            return;
        }

        const defaultSettings = {
            model_fixed: normalizedAdvancedSettings?.model_fixed || [],
            model_tracked: normalizedAdvancedSettings?.model_tracked || [],
            model_classes: normalizedAdvancedSettings?.model_classes || [],
            model: normalizedAdvancedSettings?.model || selectedModel,
            model_options: normalizedAdvancedSettings?.model_options || {},
            model_alarms: Array.isArray(normalizedAdvancedSettings?.model_alarms) ? normalizedAdvancedSettings.model_alarms : [],
            model_count: Array.isArray(normalizedAdvancedSettings?.model_count) ? normalizedAdvancedSettings.model_count : [],
            union: normalizedAdvancedSettings?.union || [],
            detectors: normalizedAdvancedSettings?.detectors || [],
            analyzers: normalizedAdvancedSettings?.analyzers || []
        };

        let nextSettings = normalizedAdvancedSettings;
        let hasChanges = false;

        if (!nextSettings || typeof nextSettings !== 'object' || Object.keys(nextSettings).length === 0) {
            nextSettings = defaultSettings;
            hasChanges = true;
        } else {
            const patches = {};
            if (!Array.isArray(nextSettings.model_alarms)) {
                patches.model_alarms = [];
            }
            if (!Array.isArray(nextSettings.model_count)) {
                patches.model_count = [];
            }
            if (Object.keys(patches).length) {
                nextSettings = {...nextSettings, ...patches};
                hasChanges = true;
            }
        }

        if (hasChanges) {
            onUpdate({...zone, advanced_settings: nextSettings});
        }
        // Note: we intentionally omit onUpdate/zone from deps to avoid perpetual reruns; normalizedAdvancedSettings captures changes.
    }, [advanced, normalizedAdvancedSettings, selectedModel]);

    const handleToggle = () => {
        setExpandedSection((prev) => {
            const next = !prev;
            if (typeof onExpandStateChange === 'function') {
                onExpandStateChange(next);
            }
            return next;
        });
    }
    useEffect(() => {
        setExpandedSection(!!expanded);
    }, [expanded]);
    useEffect(() => {
        if (!focusEventId) {
            return;
        }
        setExpandedSection(isFocused);
    }, [focusEventId, isFocused]);
    const getInitial = (advancedVal, adv, simple) => {
        return advanced ? adv ?? [] : simple ?? [];
    };

    const [selectedCount, setSelectedCount] = useState(
        ensureIdList(zone.tags ?? [])
    );

    const [selectedAlarms, setSelectedAlarms] = useState(
        ensureIdList(zone.alarms ?? [])
    );

    const [selectedAdvancedAlarms, setSelectedAdvancedAlarms] = useState(
        zone.advanced ? ensureIdList(normalizedAdvancedSettings?.model_alarms) : []
    );
    const getTagById = (id) => normalizedAllTags.find((tag) => tag?.id?.toString() == id.toString());

    const renderTagChips = (ids) =>
        ids.map((id) => {
            const tag = getTagById(id);
            return tag ? (
                <Chip
                    key={id}
                    label={tag.name}
                    style={{color: '#fff', backgroundColor: tag.color || '#ddd', marginRight: 4}}
                />
            ) : null;
        });
    useEffect(() => {
        // go over alltags and just filter the trackedtags as showCountTags
        const countTags = normalizedAllTags.filter((tag) => trackedTagIds.includes(tag?.id?.toString()));
        setShowCountTags(countTags);
        // console.log("Show Count Tags:", countTags);
    }, [trackedTagIds, normalizedAllTags]);
    useEffect(() => {
        setSelectedCount(ensureIdList(zone.tags ?? []));
    }, [zone.tags]);
    useEffect(() => {
        // go over alltags and just filter the trackedtags as showAlarmTags
        const alarmTags = normalizedAllTags.filter((tag) => selectedTagIds.includes(tag?.id?.toString()));
        setShowAlarmTags(alarmTags);
        // console.log("Show Alarm Tags:", alarmTags);
    }, [selectedTagIds, normalizedAllTags]);
    useEffect(() => {
        setSelectedAlarms(ensureIdList(zone.alarms ?? []));
    }, [zone.alarms]);

    useEffect(() => {
        console.log("Selected Count:", selectedCount);
        console.log("Selected Alarms:", selectedAlarms);
        console.log("Zone Data:", zone);
        console.log("All Tags:", allTags);
        console.log("Tracked Tags:", trackedTags);
        console.log("Selected Advanced Alarms:", selectedAdvancedAlarms);
    }, [allTags, selectedAdvancedAlarms, selectedAlarms, selectedCount, trackedTags, zone]);

    useEffect(() => {
        setSelectedAdvancedAlarms(ensureIdList(normalizedAdvancedSettings?.model_alarms));
    }, [normalizedAdvancedSettings]);

    const AdvancedBGColor = advanced ? '#f3fdf1!important' : '#fff';
    const advanced_settings = normalizedAdvancedSettings;
    const unions = ensureArray(advanced_settings?.union);
    const detectors = ensureArray(advanced_settings?.detectors);
    const analyzers = ensureArray(advanced_settings?.analyzers);

    const advancedModelCount = ensureArray(advanced_settings?.model_count);
    const advancedModelClassIds = ensureIdList(advanced_settings?.model_classes);
    const advancedAnalyzerClassIds = useMemo(() => {
        return Array.from(new Set(analyzers
            .map((analyzer) => analyzer?.class?.toString?.() ?? null)
            .filter(Boolean)));
    }, [analyzers]);
    const advancedDetectorClassIds = useMemo(() => {
        return Array.from(new Set(detectors.flatMap((detector) => ([
            detector?.class,
            ...ensureIdList(detector?.classes),
            ...ensureIdList(detector?.alarms),
            ...ensureIdList(detector?.no_alarms),
            ...ensureIdList(detector?.buffering_image),
        ])
            .map((value) => value?.toString?.() ?? null)
            .filter(Boolean))));
    }, [detectors]);
    const activeCountLength = selectedCount.length + advancedModelCount.length;
    const activeAlarmsLength = selectedAlarms.length + (selectedAdvancedAlarms?.length || 0);
    const [isDirectionEditing, setIsDirectionEditing] = useState(false);
    const directionOptions = useMemo(() => {
        const mergedIds = Array.from(new Set([
            ...selectedCount,
            ...ensureIdList(advancedModelCount),
        ]));

        return mergedIds
            .map((id) => getTagById(id))
            .filter(Boolean)
            .reduce((carry, tag) => {
                if (carry.some((entry) => entry.name === tag.name)) {
                    return carry;
                }

                carry.push(tag);
                return carry;
            }, []);
    }, [advancedModelCount, selectedCount, normalizedAllTags]);
    const directionModesByClass = useMemo(() => {
        return currentDirectionConfig?.counts_dir && typeof currentDirectionConfig.counts_dir === 'object'
            ? currentDirectionConfig.counts_dir
            : {};
    }, [currentDirectionConfig]);
    const gpioClassOptions = useMemo(() => {
        const sourceIds = Array.from(new Set([
            ...selectedTagIds,
            ...advancedModelClassIds,
            ...advancedAnalyzerClassIds,
            ...advancedDetectorClassIds,
        ]));

        return Array.from(new Set(sourceIds))
            .map((id) => getTagById(id))
            .filter(Boolean)
            .reduce((carry, tag) => {
                if (!carry.some((entry) => entry.name === tag.name)) {
                    carry.push(tag);
                }
                return carry;
            }, []);
    }, [advancedAnalyzerClassIds, advancedDetectorClassIds, advancedModelClassIds, normalizedAllTags, selectedTagIds]);
    const gpioInfoTooltip = (
        <Box sx={{ maxWidth: 320 }}>
            <Typography variant="body2">Gate selection applies to this zone only.</Typography>
            <Typography variant="body2">Delay is used only by the flux before opening the gate.</Typography>
            <Typography variant="body2">Trigger and free class lists include the classes available in this zone from the base model, advanced zone model, analyzers, and detectors.</Typography>
            <Typography variant="body2">Free classes open the gate without analysis.</Typography>
        </Box>
    );
    const selectedGateOption = useMemo(() => {
        if (!currentGpioConfig?.id) {
            return null;
        }

        return gpioGates.find((gate) => Number(gate.id) === Number(currentGpioConfig.id)) || null;
    }, [currentGpioConfig, gpioGates]);

    const linkedTrafficLights = trafficLights.filter((light) => (zone?.meta?.traffic_light_ids || []).map(Number).includes(Number(light.id)));
    const controlOptions = [
        ...gpioGates.map((gate) => ({ ...gate, kind: 'gate' })),
        ...trafficLights.map((light) => ({ ...light, kind: 'traffic' })),
    ];
    const selectedControls = controlOptions.filter((control) => control.kind === 'gate'
        ? Number(control.id) === Number(currentGpioConfig.id)
        : linkedTrafficLights.some((light) => Number(light.id) === Number(control.id)));

    const commitDirectionUpdate = useCallback((nextDirection) => {
        const meta = zone?.meta && typeof zone.meta === 'object' ? { ...zone.meta } : {};
        meta.direction = nextDirection;
        onUpdate({ ...zone, direction: nextDirection, meta });
    }, [onUpdate, zone]);
    const commitGpioUpdate = useCallback((nextGpio) => {
        const meta = zone?.meta && typeof zone.meta === 'object' ? { ...zone.meta } : {};
        meta.gpio = nextGpio;
        onUpdate({ ...zone, gpio: nextGpio, meta });
    }, [onUpdate, zone]);

    useEffect(() => {
        const handleDirectionEditorState = (event) => {
            const zoneIndex = Number(event?.detail?.zoneIndex);
            if (zoneIndex !== index) {
                return;
            }

            setIsDirectionEditing(event?.detail?.active === true);
        };

        window.addEventListener('zones:direction:editor-state', handleDirectionEditorState);
        return () => {
            window.removeEventListener('zones:direction:editor-state', handleDirectionEditorState);
        };
    }, [index]);

    useEffect(() => {
        if (!hasZoneDirection(currentDirectionConfig)) {
            return;
        }

        const allowedNames = new Set(directionOptions.map((tag) => tag.name));
        const filteredCountsDir = Object.entries(directionModesByClass).reduce((carry, [className, mode]) => {
            if (!allowedNames.has(className)) {
                return carry;
            }
            carry[className] = mode;
            return carry;
        }, {});

        if (JSON.stringify(filteredCountsDir) === JSON.stringify(directionModesByClass)) {
            return;
        }

        commitDirectionUpdate({
            ...currentDirectionConfig,
            counts_dir: filteredCountsDir,
        });
    }, [commitDirectionUpdate, currentDirectionConfig, directionModesByClass, directionOptions]);

    const handleJumpToCanvas = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }

        if (typeof document !== 'undefined') {
            const canvasElement = document.getElementById('canvas');
            const fallbackAnchor = document.getElementById('topvideo');
            const targetElement = canvasElement || fallbackAnchor;

            if (targetElement) {
                const preferredOffset = 70;
                const rect = targetElement.getBoundingClientRect();
                const absoluteTop = rect.top + window.scrollY - preferredOffset;
                window.scrollTo({top: Math.max(absoluteTop, 0), behavior: 'smooth'});
            } else if (typeof window.location !== 'undefined') {
                window.location.hash = 'topvideo';
            }
        }

        if (!Number.isInteger(resolvedZoneIndex)) {
            return;
        }

        let handled = false;
        if (window.prototypefabric && typeof window.prototypefabric.focusPolygon === 'function') {
            try {
                handled = window.prototypefabric.focusPolygon(resolvedZoneIndex, {flash: true}) === true;
            } catch (error) {
                console.warn('Unable to focus polygon via prototypefabric', error);
            }
        }

        if (!handled) {
            window.dispatchEvent(new CustomEvent('zones:canvas:focus', {detail: {index: resolvedZoneIndex}}));
        }
    }, [resolvedZoneIndex]);

    /**
     * Opens the promotion dialog with the default duration/frequency values.
     */
    const handleOpenPromotionDialog = useCallback(() => {
        setPromotionConfig(defaultPromotionConfig);
        setPromotionDialogOpen(true);
    }, []);

    /**
     * Sends the selected simple alarms to the manager promotion callback.
     */
    const handleConfirmPromotion = useCallback(() => {
        if (typeof onPromoteSimpleAlarms === 'function') {
            onPromoteSimpleAlarms(index, {
                min_duration: promotionConfig.min_duration,
                alarm_frequency: promotionConfig.alarm_frequency === '' ? null : promotionConfig.alarm_frequency,
            });
        }
        setPromotionDialogOpen(false);
    }, [index, onPromoteSimpleAlarms, promotionConfig.alarm_frequency, promotionConfig.min_duration]);


    return (
        <Grid item xs={expandedSection?12:6} sm={expandedSection?12:4} md={expandedSection?12:4} lg={expandedSection?12:4} xl={expandedSection?12:3} sx={{padding: 0.5}}>
          <Card
              id={`zona-added-${domIndex}`}
              data-zone-original-index={domIndex}
              variant="outlined"
              className={"zona-block zona-border col-sm-12"}
              sx={{mb: 0, borderRadius: 2, backgroundColor: AdvancedBGColor}}>
            {!expandedSection && (<CardContent onDoubleClick={handleToggle}>

                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
                     className="col-sm-12">
                    <Typography variant="subtitle2" className="css-zona" sx={{fontSize: '1.05rem', fontWeight: 600}}>
                        {zone.name || `Zone `+(inputIndex+1)}
                    </Typography>
                    <Tooltip title={expandedSection ? 'Hide Details' : 'Show Full Details'}>
                        <IconButton size="small" onClick={handleToggle}>
                            {expandedSection ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon  fontSize="small"/>}
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center'}}>
                    {selectedControls.map((control) => <Chip key={`${control.kind}-${control.id}`} size="small" variant="outlined"
                        label={`${control.kind === 'gate' ? 'Gate' : 'Traffic light'}: ${control.name}`} />)}
                    {zone.advanced && (
                        <Tooltip title="Advanced Enabled">
                            <SettingsIcon fontSize="small"/>
                        </Tooltip>
                    )}

                    {zone.drawZone && (
                        <Tooltip title="Zone Shape Defined">
                            <EditIcon fontSize="small"/>
                        </Tooltip>
                    )}

                    {isReperSelected && (
                        <Tooltip title="Analyze center applies to this zone">
                            <RadioButtonChecked fontSize="small" color="warning" />
                        </Tooltip>
                    )}

                    {isDirectionEnabled && (
                        <Tooltip title="Directional count is enabled">
                            <TrendingFlat fontSize="small" color="info" />
                        </Tooltip>
                    )}

                    {!!activeCountLength && (
                        <Tooltip title={`Count: ${activeCountLength}`}>
                            <Badge badgeContent={activeCountLength} size="small" color="error">

                                <PeopleIcon fontSize="small"/>
                            </Badge>
                        </Tooltip>
                    )}

                    {!!activeAlarmsLength && (
                        <Tooltip title={`Alarms: ${activeAlarmsLength}`}>
                            <Badge badgeContent={activeAlarmsLength} size="small" color="error">
                                <NotificationsIcon fontSize="small"/>
                            </Badge>
                        </Tooltip>
                    )}


                    {!!unions.length && (<Tooltip title={`Unions: ${unions.length}`}>
                        <Badge badgeContent={unions.length} size="small" color="secondary">
                            <MergeIcon fontSize="small"/>
                        </Badge>
                    </Tooltip>)}

                    {!!analyzers.length && (<Tooltip title={`Analyzers: ${analyzers.length}`}>
                        <Badge badgeContent={analyzers.length} size="small" color="primary">
                            <InsightsIcon fontSize="small"/>
                        </Badge>
                    </Tooltip>)}
                    {!!detectors.length && (<Tooltip title={`Detectors: ${detectors.length}`}>
                        <Badge badgeContent={detectors.length} size="small" color="secondary">
                            <SensorsIcon fontSize="small"/>
                        </Badge>
                    </Tooltip>)}

                </Box>
            </CardContent>)}
            {expandedSection && (<CardContent>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={12} md={12} lg={1} xl={1}>
                        <Typography variant="subtitle2" className="css-zona" sx={{fontSize: '1.05rem', fontWeight: 600}}>
                            {zone.name || `Zone `+(inputIndex+1)}
                        </Typography>

                    </Grid>

                    <Grid item xs={6} sm={6} md={1} lg={1} xl={1}>
                        <FormControlLabel size="small"
                                          control={
                                              <Switch
                                                  checked={advanced}
                                                  size="small"
                                                  color="primary"
                                                  disabled={disabled}
                                                  onChange={(e) => {
                                                      setAdvanced(e.target.checked);
                                                      onUpdate({...zone, advanced_flag: e.target.checked ? 1 : 0});
                                                  }}
                                              />
                                          }
                                          label={
                                              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                  Advanced
                                              </Typography>
                                          }
                                          labelPlacement="top"
                        />
                    </Grid>

                    <Grid item xs={6} sm={6} md={2} lg={1} xl={1}>
                        <FormControlLabel size="small"
                                          control={
                                              <Switch
                                                  checked={drawZone}
                                                  size="small"
                                                  color="secondary"
                                                  disabled={disabled}
                                                  onChange={(e) => {
                                                      setDrawZone(e.target.checked);
                                                      onUpdate({...zone, draw_zone_flag: e.target.checked ? 1 : 0});
                                                  }}
                                              />
                                          }
                                          label={
                                              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                  Draw zone
                                              </Typography>
                                          }

                                          labelPlacement="top"
                        />
                    </Grid>

                    <Grid item xs={6} sm={6} md={2} lg={1} xl={1}>
                        <FormControlLabel size="small"
                                          control={
                                              <Switch
                                                  checked={isDirectionEnabled}
                                                  size="small"
                                                  color="info"
                                                  disabled={disabled}
                                                  onChange={(e) => {
                                                      onToggleDirectionZone?.(index, e.target.checked);
                                                      if (e.target.checked) {
                                                          onEditDirectionZone?.(index);
                                                      }
                                                  }}
                                              />
                                          }
                                          label={
                                              <Typography variant="caption" sx={{ fontWeight: 500 }}>
                                                  Direction
                                              </Typography>
                                          }
                                          labelPlacement="top"
                        />
                    </Grid>

                   <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
                       {!disabled && (   <Box display="flex" justifyContent="flex-end">

                            {advanced && (<Tooltip title="Advanced Settings">
                                <IconButton onClick={() => onSettings(index)} size="small">
                                    <Settings fontSize="small"/>
                                </IconButton>
                            </Tooltip>)}
                            <Tooltip title="Schedule">
                                <IconButton onClick={() => onSchedule(index)} size="small">
                                    <AccessTime fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Clone">
                                <IconButton onClick={() => onClone(index)} size="small">
                                    <ContentCopy fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <IconButton onClick={() => onDelete(index)} color="error" size="small">
                                    <Delete fontSize="small"/>
                                </IconButton>
                            </Tooltip>
                            <Tooltip title="Go to Top">
                                <IconButton
                                    color="error"
                                    size="small"
                                    onClick={handleJumpToCanvas}
                                >
                                    <VerticalAlignTop fontSize="small" />
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={isReperSelected ? 'Stop using this zone for Analyze center' : 'Use this zone for Analyze center'}>
                                <IconButton
                                    color={isReperSelected ? 'warning' : 'default'}
                                    size="small"
                                    onClick={() => onToggleReperZone?.(index)}
                                >
                                    {isReperSelected ? <RadioButtonChecked fontSize="small" /> : <RadioButtonUnchecked fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={isDirectionEnabled ? 'Edit direction arrow' : 'Enable directional count and draw the arrow'}>
                                <IconButton
                                    color={isDirectionEnabled ? 'info' : 'default'}
                                    size="small"
                                    onClick={() => {
                                        if (isDirectionEditing) {
                                            window.dispatchEvent(new CustomEvent('zones:direction:editor:done', { detail: { zoneIndex: index } }));
                                            return;
                                        }
                                        onEditDirectionZone?.(index);
                                    }}
                                >
                                    {isDirectionEditing ? <Check fontSize="small" /> : <TrendingFlat fontSize="small" />}
                                </IconButton>
                            </Tooltip>
                            <Tooltip title={expandedSection ? 'Hide Details' : 'Show Full Details'}>
                                <IconButton size="small" onClick={handleToggle}>
                                    {expandedSection ? <ExpandLessIcon fontSize="small"/> : <ExpandMoreIcon  fontSize="small"/>}
                                </IconButton>
                            </Tooltip>
                        </Box>)
                       }
                    </Grid>
                    <Grid item xs={12}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item xs={12} sm={6} md={3} lg={3} xl={3}>
                                <Autocomplete
                                    size="small"
                                    limitTags={2}
                                    multiple
                                    disabled={disabled}
                                    options={showCountTags}
                                    value={showCountTags.filter(tag => selectedCount.includes(tag?.id?.toString()))}
                                    getOptionLabel={(tag) => tag.name}
                                    onChange={(e, newValue) => {
                                        const ids = newValue.map((t) => t.id.toString());
                                        setSelectedCount(ids);
                                        onUpdate({...zone, count: ids});
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Count" variant="outlined"/>}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={2} lg={2} xl={2}>
                                <Autocomplete
                                    size="small"
                                    multiple
                                    disabled={disabled}
                                    limitTags={2}
                                    options={showAlarmTags}
                                    value={showAlarmTags.filter(tag => selectedAlarms.includes(tag?.id?.toString()))}
                                    getOptionLabel={(tag) => tag.name}
                                    onChange={(e, newValue) => {
                                        const ids = newValue.map((t) => t.id.toString());
                                        setSelectedAlarms(ids);
                                        onUpdate({...zone, alarms: ids});
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Alarms" size="small" variant="outlined"/>}
                                />
                            </Grid>
                            {controlOptions.length > 0 && <Grid item xs={12} sm={6} md={4}>
                                <Autocomplete multiple size="small" limitTags={2} disabled={disabled} options={controlOptions} value={selectedControls}
                                    groupBy={(control) => control.kind === 'gate' ? 'Gates' : 'Traffic lights'}
                                    getOptionLabel={(control) => `${control.kind === 'gate' ? 'Gate' : 'Traffic light'}: ${control.name}`}
                                    isOptionEqualToValue={(a, b) => a.kind === b.kind && Number(a.id) === Number(b.id)}
                                    onChange={(_, controls) => {
                                        const gate = controls.filter((control) => control.kind === 'gate').at(-1);
                                        const gpio = gate ? { id: Number(gate.id), name: gate.name, delay: currentGpioConfig?.delay ?? 0, classes: currentGpioConfig?.classes || [], free_classes: currentGpioConfig?.free_classes || [] } : {};
                                        onUpdate({ ...zone, gpio, meta: { ...(zone.meta || {}), gpio,
                                            traffic_light_ids: controls.filter((control) => control.kind === 'traffic').map((control) => Number(control.id)),
                                        } });
                                    }}
                                    renderInput={(params) => <TextField {...params} label="Gate & traffic lights" />} />
                            </Grid>}
                            {selectedGateOption && (
                                <Grid item xs={12} md={7} lg={7} xl={7}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={2}>
                                            <TextField
                                                size="small"
                                                fullWidth
                                                type="number"
                                                disabled={disabled}
                                                label="GPIO delay (s)"
                                                value={currentGpioConfig?.delay ?? 0}
                                                onChange={(event) => {
                                                    const rawValue = Number(event.target.value);
                                                    commitGpioUpdate({
                                                        id: Number(selectedGateOption.id),
                                                        name: selectedGateOption.name,
                                                        delay: Number.isFinite(rawValue) && rawValue >= 0 ? Number(rawValue.toFixed(2)) : 0,
                                                        classes: currentGpioConfig?.classes || [],
                                                        free_classes: currentGpioConfig?.free_classes || [],
                                                    });
                                                }}
                                                inputProps={{ min: 0, step: 0.1 }}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <Autocomplete
                                                size="small"
                                                multiple
                                                disabled={disabled}
                                                options={gpioClassOptions}
                                                value={gpioClassOptions.filter((tag) => (currentGpioConfig?.classes || []).includes(tag.name))}
                                                getOptionLabel={(tag) => tag.name}
                                                onChange={(event, value) => {
                                                    commitGpioUpdate({
                                                        id: Number(selectedGateOption.id),
                                                        name: selectedGateOption.name,
                                                        delay: currentGpioConfig?.delay ?? 0,
                                                        classes: value.map((entry) => entry.name),
                                                        free_classes: currentGpioConfig?.free_classes || [],
                                                    });
                                                }}
                                                renderInput={(params) => <TextField {...params} label="GPIO trigger classes" variant="outlined" />}
                                            />
                                        </Grid>
                                        <Grid item xs={12} md={5}>
                                            <Autocomplete
                                                size="small"
                                                multiple
                                                disabled={disabled}
                                                options={gpioClassOptions}
                                                value={gpioClassOptions.filter((tag) => (currentGpioConfig?.free_classes || []).includes(tag.name))}
                                                getOptionLabel={(tag) => tag.name}
                                                onChange={(event, value) => {
                                                    commitGpioUpdate({
                                                        id: Number(selectedGateOption.id),
                                                        name: selectedGateOption.name,
                                                        delay: currentGpioConfig?.delay ?? 0,
                                                        classes: currentGpioConfig?.classes || [],
                                                        free_classes: value.map((entry) => entry.name),
                                                    });
                                                }}
                                                renderInput={(params) => <TextField {...params} label="GPIO free classes" variant="outlined" />}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>
                            )}
                        </Grid>
                    </Grid>
                    {isDirectionEnabled && (
                        <Grid item xs={12}>
                            {directionOptions.length > 0 ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                                        {isDirectionEditing ? 'Editing arrow. Click the blue check icon when done.' : 'Directional values:'}
                                    </Typography>
                                    {directionOptions.map((tag) => (
                                        <TextField
                                            key={`direction-${tag.id}`}
                                            select
                                            size="small"
                                            label={`${tag.name} direction`}
                                            sx={{ minWidth: { xs: '100%', sm: 170, lg: 170 } }}
                                            value={directionModesByClass[tag.name] || ''}
                                            onChange={(event) => {
                                                const nextCountsDir = { ...directionModesByClass };
                                                const nextValue = event.target.value;
                                                if (!nextValue) {
                                                    delete nextCountsDir[tag.name];
                                                } else {
                                                    nextCountsDir[tag.name] = nextValue;
                                                }

                                                commitDirectionUpdate({
                                                    ...currentDirectionConfig,
                                                    counts_dir: nextCountsDir,
                                                });
                                            }}
                                        >
                                            <MenuItem value="">{directionModeLabel('')}</MenuItem>
                                            <MenuItem value="forward">{directionModeLabel('forward')}</MenuItem>
                                            <MenuItem value="backward">{directionModeLabel('backward')}</MenuItem>
                                            <MenuItem value="both">{directionModeLabel('both')}</MenuItem>
                                        </TextField>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="caption" color="text.secondary">
                                    Select at least one count class in this zone to configure directional reporting.
                                </Typography>
                            )}
                        </Grid>
                    )}
                    {!disabled && !hidePromotionAction && selectedAlarms.length > 0 && (
                        <Grid item xs={12}>
                            <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap'}}>
                                <Typography variant="caption" color="text.secondary">
                                    Promote these simple alarms into Alarm Management and save the page.
                                </Typography>
                                <Button variant="outlined" size="small" onClick={handleOpenPromotionDialog}>
                                    Promote To Alarm Management
                                </Button>
                            </Box>
                        </Grid>
                    )}
                    {selectedHornId >= 0 && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                multiline
                                minRows={3}
                                label="Zone audio message"
                                value={currentAudioMessage}
                                onChange={(event) => {
                                    const nextAudioMessage = normalizeZoneAudioMessage(event.target.value);
                                    const nextMeta = zone?.meta && typeof zone.meta === 'object' ? { ...zone.meta } : {};
                                    nextMeta.audio_message = nextAudioMessage;

                                    onUpdate({
                                        ...zone,
                                        meta: nextMeta,
                                    });
                                }}
                                inputProps={{ maxLength: 2000 }}
                                helperText={audioMessageEnabled
                                    ? 'Write the message in Romanian using diacritics. Leave it empty to export "".'
                                    : 'Audio message is disabled in Start & Stop mode.'}
                                disabled={disabled || !audioMessageEnabled}
                            />
                        </Grid>
                    )}
                    <Grid item xs={12}>
                        <Grid container spacing={1}>

                            <Grid item xs={12} md={12}>
                                <ZoneSummaryAdvanced
                                    advancedToggle={advanced}
                                    countBasic={selectedCount}
                                    alarmsBasic={selectedAlarms}
                                    modelAlarms={selectedAdvancedAlarms}
                                    getTagById={getTagById}
                                    advanced={zone.advanced_settings}
                                    markerSets={markerSets}
                                    gpioConfig={currentGpioConfig}
                                    trafficLights={linkedTrafficLights}
                                />

                            </Grid>
                            <Grid item xs={12} md={12}>
                                <ScheduleSummary schedule={zone.armed || []}/>
                            </Grid>
                        </Grid>
                    </Grid>



                </Grid>

            </CardContent>)}
            {/* Blade-compatible hidden inputs */}
            <input type="hidden" name="zones_id[]" value={zone.id} className="zones_id"/>
            <input type="hidden" name="zones_points[]" value={JSON.stringify(zone.polygon || [])}
                   className="zones_points"/>
         <input type="hidden" name="zones_scaled_points[]" value={JSON.stringify(zone.scaled_polygon || [])}
             className="zones_scaled_points"/>
         <input type="hidden" name="zones_original_width[]" value={zone.original_width ?? ''}
             className="zones_original_width"/>
         <input type="hidden" name="zones_original_height[]" value={zone.original_height ?? ''}
             className="zones_original_height"/>
         <input type="hidden" name="zones_version[]" value={zone.version ?? ''}
             className="zones_version"/>
            <input type="hidden" name="zones_meta[]" value={zoneMetaValue} className="zones_meta"/>
            <input type="hidden" name="zones_armed[]" value={JSON.stringify(zone.armed || [])} className="zones_armed"/>
            <input type="hidden" name="zones_advanced[]" value={JSON.stringify(zone.advanced_settings || {})}
                   className="zones_advanced"/>
            <input type="hidden" name={`zones_advanced_flag_${inputIndex}`} value={advanced ? 1 : 0}
                   className="zones_advanced_flag"/>
            <input type="hidden" name={`zones_draw_zone_flag_${inputIndex}`} value={drawZone ? 1 : 0}
                   className="zones_draw_zone_flag"/>

            {/* Hidden count and alarm values for Laravel form submission */}
            {selectedCount.map((id) => (
                <input
                    type="hidden"
                    key={`count-${id}`}
                    name={`zones_tags_${inputIndex}[]`}
                    value={id}
                />
            ))}

            {selectedAlarms.map((id) => (
                <input
                    type="hidden"
                    key={`alarm-${id}`}
                    name={`zones_alarms_${inputIndex}[]`}
                    value={id}
                />
            ))}
            <Dialog open={promotionDialogOpen} onClose={() => setPromotionDialogOpen(false)}>
                <DialogTitle>Promote Simple Alarms</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{mb: 2}}>
                        This will move the selected simple zone alarms into Alarm Management, clear them from the zone, and submit the page for a full reload.
                    </Typography>
                    <TextField
                        margin="dense"
                        type="number"
                        label="Minimum scenario duration (seconds)"
                        fullWidth
                        inputProps={{min: 1}}
                        value={promotionConfig.min_duration}
                        onChange={(event) => {
                            setPromotionConfig((current) => ({
                                ...current,
                                min_duration: event.target.value,
                            }));
                        }}
                    />
                    <TextField
                        margin="dense"
                        type="number"
                        label="Alarm frequency (seconds, optional)"
                        fullWidth
                        inputProps={{min: 0}}
                        value={promotionConfig.alarm_frequency}
                        onChange={(event) => {
                            setPromotionConfig((current) => ({
                                ...current,
                                alarm_frequency: event.target.value,
                            }));
                        }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPromotionDialogOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleConfirmPromotion}>
                        Promote And Reload
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
        </Grid>
    );
};

export default ZoneCard;
